import { Skeleton, Stack } from '@mantine/core';
import type { ReactNode } from 'react';
import { QueryError } from './QueryError';

/**
 * The loading / error / empty / active ladder, in one place.
 *
 * The rule it exists to enforce: **a query that failed must never fall through
 * to the empty state.** "No connections yet" and "we couldn't reach the server"
 * are different facts, and showing the first when the second is true tells the
 * user something false about their own account. 33 vendor routes currently
 * issue `useQuery` with no `isError` branch at all, so today they all lie.
 *
 * Order is load-bearing: pending wins over error (a refetch after a failure
 * should show a skeleton, not keep the old error), and error wins over empty.
 *
 * Deliberately not a render-prop over the query object: callers pass the three
 * booleans they already have, so this works with tRPC queries, plain TanStack
 * queries, and derived state assembled from several of them.
 */
export interface QueryBoundaryProps {
  isPending: boolean;
  isError: boolean;
  /** Underlying failure, shown as supporting detail under the plain-language message. */
  error?: { message: string } | null;
  /** Plain-language, user-facing: "Couldn't load your connections." */
  errorMessage: string;
  onRetry?: () => void;
  /**
   * True when the query succeeded but returned nothing. Pass the `EmptyState`
   * for this surface as `empty`; omit both to render `children` regardless.
   */
  isEmpty?: boolean;
  empty?: ReactNode;
  /** Skeleton shown while pending. Defaults to three stacked bars. */
  loading?: ReactNode;
  children: ReactNode;
}

export function QueryBoundary({
  isPending,
  isError,
  error,
  errorMessage,
  onRetry,
  isEmpty,
  empty,
  loading,
  children,
}: QueryBoundaryProps) {
  if (isPending) {
    return (
      <>
        {loading ?? (
          <Stack gap="xs">
            <Skeleton height={72} radius="md" />
            <Skeleton height={72} radius="md" />
            <Skeleton height={72} radius="md" />
          </Stack>
        )}
      </>
    );
  }

  if (isError) {
    return <QueryError message={errorMessage} detail={error?.message} onRetry={onRetry} />;
  }

  if (isEmpty && empty) return <>{empty}</>;

  return <>{children}</>;
}
